import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const SHOPIFY_URL = "https://parfumalgerie.shop";

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#FAFAFA]"
    >
      {/* Background Glow */}
      <div className="absolute inset-0 hero-glow" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-screen pt-24 pb-16">
          {/* Left: Text Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="order-2 lg:order-1 text-center lg:text-left"
          >
            <motion.span
              variants={itemVariants}
              className="inline-block font-montserrat text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-6"
            >
              Collection Exclusive
            </motion.span>

            <motion.h1
              variants={itemVariants}
              className="font-playfair text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.1] text-[#1A1A1A] mb-6"
            >
              L'Art du{" "}
              <span className="italic font-cormorant">Parfum</span>
              <br />
              en Algérie
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="font-montserrat text-base md:text-lg text-[#666] leading-relaxed tracking-wide max-w-lg mx-auto lg:mx-0 mb-10"
            >
              Découvrez notre collection raffinée de parfums de luxe, 
              soigneusement sélectionnés pour éveiller vos sens et 
              sublimer votre élégance au quotidien.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a
                href={SHOPIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-3 group"
                data-testid="hero-shop-btn"
              >
                Découvrir la Collection
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#products"
                className="btn-secondary inline-flex items-center justify-center"
                data-testid="hero-explore-btn"
              >
                Explorer
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={itemVariants}
              className="flex items-center justify-center lg:justify-start gap-12 mt-16 pt-8 border-t border-black/5"
            >
              <div className="text-center lg:text-left">
                <span className="block font-playfair text-3xl md:text-4xl text-[#1A1A1A]">
                  2500+
                </span>
                <span className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#999]">
                  Parfums
                </span>
              </div>
              <div className="text-center lg:text-left">
                <span className="block font-playfair text-3xl md:text-4xl text-[#1A1A1A]">
                  50K+
                </span>
                <span className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#999]">
                  Clients
                </span>
              </div>
              <div className="text-center lg:text-left">
                <span className="block font-playfair text-3xl md:text-4xl text-[#1A1A1A]">
                  58
                </span>
                <span className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#999]">
                  Wilayas
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative aspect-[3/4] max-w-md mx-auto lg:max-w-none">
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-full h-full border border-[#D4AF37]/30 rounded-sm" />
              <div className="absolute -bottom-4 -left-4 w-full h-full bg-[#E0E5DF]/30 rounded-sm" />
              
              {/* Main Image */}
              <div className="relative overflow-hidden rounded-sm shadow-hover">
                <motion.img
                  src="https://images.unsplash.com/photo-1729101807924-3446ca9aa480?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Parfum de luxe"
                  className="w-full h-full object-cover animate-float"
                  style={{ animationDuration: "8s" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-white shadow-hover px-6 py-4 rounded-sm"
              >
                <span className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#999] block mb-1">
                  Livraison
                </span>
                <span className="font-playfair text-lg text-[#1A1A1A]">
                  Partout en Algérie
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-[#1A1A1A]/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1 h-2 bg-[#1A1A1A]/50 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
